/**
 * useAuth.js — React hook for dual-account authentication state
 *
 * Manages the authentication lifecycle for both calendar accounts.
 * Provides login/logout functions and tracks connection status
 * for each account independently.
 *
 * State shape per account:
 *   { account: <MSAL account>, profile: { displayName, mail }, status: 'disconnected'|'connected'|'error', error: null }
 */

import { useState, useEffect, useCallback } from 'react';
import { initializeMsal, loginWithPopup, logoutAccount, acquireToken } from '../services/authService';
import { getUserProfile } from '../services/graphService';

/**
 * Custom hook for managing dual Microsoft 365 account authentication.
 *
 * Returns an object with:
 *   - cal1: Auth state for Calendar 1 (Invero)
 *   - cal2: Auth state for Calendar 2 (Work 2)
 *   - loginCal1(): Trigger login for Invero
 *   - loginCal2(): Trigger login for Work 2
 *   - logoutCal1(): Disconnect Invero
 *   - logoutCal2(): Disconnect Work 2
 *   - isReady: Whether MSAL has finished initializing
 *   - bothConnected: Whether both accounts are authenticated
 */
export function useAuth() {
  const [isReady, setIsReady] = useState(false);
  const [initError, setInitError] = useState(null);

  // Auth state for each calendar — tracked independently
  const [cal1, setCal1] = useState({
    account: null,
    profile: null,
    status: 'disconnected', // 'disconnected' | 'connecting' | 'connected' | 'error'
    error: null,
  });

  const [cal2, setCal2] = useState({
    account: null,
    profile: null,
    status: 'disconnected',
    error: null,
  });

  // Initialize MSAL on mount — must happen before any login attempts
  useEffect(() => {
    initializeMsal()
      .then(() => setIsReady(true))
      .catch((err) => {
        console.error('MSAL initialization failed:', err);
        setInitError(err.message);
      });
  }, []);

  /**
   * Generic login handler for either calendar account.
   * Triggers popup, fetches user profile, updates state.
   *
   * @param {Function} setState - setCal1 or setCal2
   * @param {string} [loginHint] - Optional email hint
   */
  const handleLogin = useCallback(async (setState, loginHint) => {
    setState((prev) => ({ ...prev, status: 'connecting', error: null }));

    try {
      // Step 1: Interactive popup login
      const account = await loginWithPopup(loginHint);

      // Step 2: Get an access token for this account
      const accessToken = await acquireToken(account);

      // Step 3: Fetch the user's profile to display name + email
      const profile = await getUserProfile(accessToken);

      setState({
        account,
        profile: {
          displayName: profile.displayName,
          mail: profile.mail || profile.userPrincipalName,
        },
        status: 'connected',
        error: null,
      });
    } catch (err) {
      console.error('Login failed:', err);
      setState((prev) => ({
        ...prev,
        status: 'error',
        error: err.message || 'Login failed. Please try again.',
      }));
    }
  }, []);

  // Login functions for each calendar
  const loginCal1 = useCallback(() => handleLogin(setCal1), [handleLogin]);
  const loginCal2 = useCallback(() => handleLogin(setCal2), [handleLogin]);

  // Logout functions — clear local state and MSAL cache for that account
  const logoutCal1 = useCallback(async () => {
    if (cal1.account) {
      try {
        await logoutAccount(cal1.account);
      } catch {
        // Logout popup may fail if blocked — still clear local state
      }
    }
    setCal1({ account: null, profile: null, status: 'disconnected', error: null });
  }, [cal1.account]);

  const logoutCal2 = useCallback(async () => {
    if (cal2.account) {
      try {
        await logoutAccount(cal2.account);
      } catch {
        // Logout popup may fail if blocked — still clear local state
      }
    }
    setCal2({ account: null, profile: null, status: 'disconnected', error: null });
  }, [cal2.account]);

  return {
    cal1,
    cal2,
    loginCal1,
    loginCal2,
    logoutCal1,
    logoutCal2,
    isReady,
    initError,
    bothConnected: cal1.status === 'connected' && cal2.status === 'connected',
  };
}
